import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Star, Quote } from 'lucide-react';
import Hero from '../components/sections/Hero';
import ServiceCard from '../components/ui/ServiceCard';
import { containerVariants, itemVariants } from '../utils/animations';
import { 
  aboutPreview, 
  featuredServices, 
  industries, 
  testimonials, 
  ctaSection
} from '../data/content';

const Home: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>SOFTCONCERN TECHNOLOGIES - AI-Driven IT Solutions</title>
        <meta name="description" content="Empowering businesses with innovative AI-driven IT solutions. Custom software development, cloud solutions, and IT consulting services." />
        <meta name="keywords" content="IT solutions, AI automation, software development, cloud solutions, IT consulting, Pune" />
        <meta property="og:title" content="SOFTCONCERN TECHNOLOGIES - AI-Driven IT Solutions" />
        <meta property="og:description" content="Empowering businesses with innovative AI-driven IT solutions. Custom software development, cloud solutions, and IT consulting services." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="/" />
      </Helmet>

      {/* Hero Section */}
      <Hero />

      {/* About Preview Section */}
      <section className="section-padding bg-white">
        <div className="container">
          <motion.div
            className="text-center max-w-4xl mx-auto"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            <motion.h2 
              className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6"
              variants={itemVariants}
            >
              {aboutPreview.title}
            </motion.h2>
            <motion.p 
              className="text-lg text-gray-600 leading-relaxed mb-8"
              variants={itemVariants}
            >
              {aboutPreview.content}
            </motion.p>
            <motion.div variants={itemVariants}>
              <Link 
                to="/about"
                className="btn-primary inline-flex items-center space-x-2"
              >
                <span>Learn More About Us</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Featured Services Section */}
      <section className="section-padding bg-gray-50">
        <div className="container">
          <motion.div
            className="text-center mb-12"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            <motion.h2 
              className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4"
              variants={itemVariants}
            >
              Our Core Services
            </motion.h2>
            <motion.p 
              className="text-lg text-gray-600 max-w-2xl mx-auto"
              variants={itemVariants}
            >
              We provide comprehensive IT solutions to help your business thrive in the digital age.
            </motion.p>
          </motion.div>

          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            {featuredServices.map((service, index) => (
              <motion.div key={service.id} variants={itemVariants}>
                <ServiceCard
                  title={service.title}
                  description={service.description}
                  icon={service.icon}
                  onClick={() => window.location.href = '/services'}
                />
              </motion.div>
            ))}
          </motion.div>

          <motion.div 
            className="text-center mt-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5 }}
          >
            <Link 
              to="/services"
              className="btn-secondary inline-flex items-center space-x-2"
            >
              <span>View All Services</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Industries We Serve Section */}
      <section className="section-padding bg-white">
        <div className="container">
          <motion.div
            className="text-center mb-12"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            <motion.h2 
              className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4"
              variants={itemVariants}
            >
              Industries We Serve
            </motion.h2>
            <motion.p 
              className="text-lg text-gray-600 max-w-2xl mx-auto"
              variants={itemVariants}
            >
              Our expertise spans across various industries, delivering tailored solutions for each sector.
            </motion.p>
          </motion.div>

          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            {industries.map((industry, index) => (
              <motion.div
                key={industry.name}
                className="card p-6 text-center group"
                variants={itemVariants}
                whileHover={{ y: -5 }}
              >
                <div className="text-4xl mb-4">{industry.icon === 'ShoppingCart' ? '🛒' : industry.icon === 'Heart' ? '❤️' : industry.icon === 'BookOpen' ? '📚' : '💳'}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {industry.name}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {industry.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="section-padding bg-primary-50">
        <div className="container">
          <motion.div
            className="text-center mb-12"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            <motion.h2 
              className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4"
              variants={itemVariants}
            >
              What Our Clients Say
            </motion.h2>
            <motion.p 
              className="text-lg text-gray-600 max-w-2xl mx-auto"
              variants={itemVariants}
            >
              Discover how we've helped businesses transform their IT operations and achieve their goals.
            </motion.p>
          </motion.div>

          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.id}
                className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
                variants={itemVariants}
                whileHover={{ y: -5, boxShadow: "0 10px 25px -3px rgba(0, 0, 0, 0.1)" }}
              >
                <div className="flex items-center justify-between mb-4">
                  <Quote className="w-8 h-8 text-primary-400" />
                  <div className="flex space-x-1">
                    {Array.from({ length: testimonial.rating }, (_, i) => (
                      <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                    ))}
                  </div>
                </div>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  "{testimonial.content}"
                </p>
                <div className="border-t border-gray-100 pt-4">
                  <p className="font-semibold text-gray-900">{testimonial.name}</p>
                  <p className="text-sm text-gray-600">{testimonial.company}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-primary-600">
        <div className="container">
          <motion.div
            className="text-center text-white"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            <motion.h2 
              className="text-3xl sm:text-4xl font-bold mb-4"
              variants={itemVariants}
            >
              {ctaSection.title}
            </motion.h2>
            <motion.p 
              className="text-xl mb-8 text-primary-100 max-w-2xl mx-auto"
              variants={itemVariants}
            >
              {ctaSection.content}
            </motion.p>
            <motion.div variants={itemVariants}>
              <Link
                to={ctaSection.buttonHref}
                className="bg-white text-primary-600 hover:bg-gray-100 font-medium py-3 px-8 rounded-lg transition-colors duration-200 inline-flex items-center space-x-2"
              >
                <span>{ctaSection.buttonText}</span>
                <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Home;